import React from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Globe, HeartHandshake, Target, Users, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export const About = () => {
  const { t } = useTranslation('about');

  return (
    <div className="bg-[#F6F1EA]">
      {/* Hero Section */}
      <section
        className="relative h-[70vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_miela-luxury/artifacts/zejvqz81_image.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            {t('hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            {t('hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#B08D57]/10 rounded-full flex items-center justify-center mb-6">
                  <Users className="text-[#B08D57]" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-[#3A312A] mb-4">{t('team.founderStory.name')}</h3>
                <p className="text-[#3A312A]/70 leading-relaxed">{t('team.founderStory.description')}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#B08D57]/10 rounded-full flex items-center justify-center mb-6">
                  <Target className="text-[#B08D57]" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-[#3A312A] mb-4">{t('team.mission.name')}</h3>
                <p className="text-[#3A312A]/70 leading-relaxed">{t('team.mission.description')}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#B08D57]/10 rounded-full flex items-center justify-center mb-6">
                  <Award className="text-[#B08D57]" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-[#3A312A] mb-4">{t('team.vision.name')}</h3>
                <p className="text-[#3A312A]/70 leading-relaxed">{t('team.vision.description')}</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#B08D57]/10 rounded-full flex items-center justify-center mb-6">
                  <TrendingUp className="text-[#B08D57]" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-[#3A312A] mb-4">{t('team.values.name')}</h3>
                <p className="text-[#3A312A]/70 leading-relaxed">{t('team.values.description')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#3A312A] mb-4">
              {t('approach.title')}
            </h2>
            <p className="text-lg text-[#3A312A]/70">{t('approach.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border border-[#E8DDD0] hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-8">
                <HeartHandshake className="text-[#B08D57] mb-6" size={40} />
                <h3 className="text-xl font-bold text-[#3A312A] mb-3">{t('approach.directRelationships.title')}</h3>
                <p className="text-[#3A312A]/70 leading-relaxed">{t('approach.directRelationships.description')}</p>
              </CardContent>
            </Card>

            <Card className="border border-[#E8DDD0] hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-8">
                <Award className="text-[#B08D57] mb-6" size={40} />
                <h3 className="text-xl font-bold text-[#3A312A] mb-3">{t('approach.qualityFirst.title')}</h3>
                <p className="text-[#3A312A]/70 leading-relaxed">{t('approach.qualityFirst.description')}</p>
              </CardContent>
            </Card>

            <Card className="border border-[#E8DDD0] hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-8">
                <Globe className="text-[#B08D57] mb-6" size={40} />
                <h3 className="text-xl font-bold text-[#3A312A] mb-3">{t('approach.culturalBridge.title')}</h3>
                <p className="text-[#3A312A]/70 leading-relaxed">{t('approach.culturalBridge.description')}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#3A312A] mb-16 text-center">
            {t('journey.title')}
          </h2>
          <div className="space-y-6 md:space-y-8">
            {['2021', '2022', '2023', '2024', '2025', '2026'].map((year) => (
              <div key={year} className="flex items-start space-x-3 sm:space-x-4 md:space-x-6 group">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-[#B08D57] rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base md:text-lg group-hover:scale-110 transition-transform duration-300">
                    {year}
                  </div>
                </div>
                <div className="flex-1 pt-2 sm:pt-4 md:pt-6">
                  <div className="bg-[#F6F1EA] p-5 sm:p-6 md:p-8 rounded-lg border border-[#E8DDD0] group-hover:border-[#B08D57] transition-colors duration-300">
                    <h3 className="text-xl sm:text-xl md:text-2xl font-bold text-[#3A312A] mb-2 sm:mb-3">{t(`journey.milestones.${year}.title`)}</h3>
                    <p className="text-sm sm:text-base text-[#3A312A]/80 leading-relaxed">{t(`journey.milestones.${year}.event`)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
